import { useState } from "react";
import { api } from "../api.js";
import { safeErrorCode } from "@smart-shikka/react";

export type CommandTarget = "browser" | "desktop" | "extension";

interface Props {
  deviceToken: string | null;
  emergencyStopped?: boolean;
}

const COMMANDS: Record<string, string> = {
  pause: "Pause the active task on this device.",
  stop: "Emergency stop — block all mutations.",
  revoke: "Revoke this device session.",
  ping: "Check connectivity and last seen time.",
};

export function MobileCommandPanel({ deviceToken, emergencyStopped }: Props) {
  const [target, setTarget] = useState<CommandTarget>("desktop");
  const [command, setCommand] = useState("pause");
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!deviceToken) {
      setStatus("Device not paired.");
      return;
    }
    if (emergencyStopped) {
      setStatus("Blocked by Emergency Stop.");
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const res = await api.mobileAction(deviceToken, command, target);
      setStatus(res.ok ? `Command '${command}' sent to ${target}.` : `Failed to send command. (${safeErrorCode(res.error)})`);
    } catch {
      setStatus("Network error.");
    } finally {
      setBusy(false);
    }
  };

  const blocked = emergencyStopped;

  return (
    <div className="ss-form" style={{ marginTop: 12 }}>
      <h3>Remote command</h3>
      {blocked ? <div className="msg error" style={{ marginBottom: 8 }}>Emergency Stop is active — commands are paused.</div> : null}
      <select value={target} onChange={(e) => setTarget(e.target.value as CommandTarget)} disabled={busy || blocked}>
        <option value="desktop">Desktop</option>
        <option value="browser">Browser</option>
        <option value="extension">Extension</option>
      </select>
      <select value={command} onChange={(e) => setCommand(e.target.value)} disabled={busy || blocked}>
        {Object.keys(COMMANDS).map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <p className="ss-muted" style={{ fontSize: 12, margin: "4px 0" }}>
        {COMMANDS[command]}
      </p>
      <button onClick={send} disabled={busy || !deviceToken || blocked} className="btn-primary">
        {busy ? "Sending..." : blocked ? "Blocked by Emergency Stop" : "Send safe command"}
      </button>
      {status ? <div className="msg info">{status}</div> : null}
    </div>
  );
}
